import { ViewModelBase, FieldType, ApiResult } from "@shoothill/core";
import { observable, action, computed, runInAction } from "mobx";
import { ProjectDirectoryModel, ProjectDirectoryModelDTO } from "./ProjectDirectoryModel";
import { AppUrls } from "AppUrls";

export class ProjectDirectoryViewModel extends ViewModelBase<ProjectDirectoryModel> {
    @observable public errorMessage: string = "";

    @observable
    private entries: ProjectDirectoryModel[] = [];

    @observable
    private currentProjectId: string = "";

    @computed
    public get directoryEntries(): ProjectDirectoryModel[] {
        return this.entries.filter((entry) => !entry.isDeleted);
    }

    @action
    public setDirectoryEntries(list: ProjectDirectoryModelDTO[]) {
        this.entries = list.map((dto) => {
            const model = new ProjectDirectoryModel();
            model.fromDto(dto);
            return model;
        });
    }

    @action
    public setProjectId(projectId: string) {
        this.currentProjectId = projectId;
        this.model.projectId = projectId;
    }

    public constructor() {
        super(new ProjectDirectoryModel(), false);
        this.setDecorators(ProjectDirectoryModel);
    }

    @action
    public cleanUp = () => {
        this.model.clear();
        this.entries = [];
    };

    @action
    public setModel(model: ProjectDirectoryModel) {
        this.model = model;
    }

    @action
    public clearModel() {
        this.model.clear();
    }

    public async isFieldValid(fieldName: keyof FieldType<ProjectDirectoryModel>): Promise<boolean> {
        const { isValid, errorMessage } = await this.validateDecorators(fieldName);
        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);
        return isValid;
    }

    public async validateModelWithLogging(): Promise<boolean> {
        console.log("Starting model validation...");
        console.log("Current model state:", {
            firstName: this.model.firstName,
            lastName: this.model.lastName,
            company: this.model.company,
            role: this.model.role,
            emailAddress: this.model.emailAddress,
            phoneNumber: this.model.phoneNumber,
            projectId: this.model.projectId,
        });

        const fields: (keyof FieldType<ProjectDirectoryModel>)[] = ["firstName", "lastName", "company", "role", "emailAddress", "phoneNumber", "projectId"];

        let isValid = true;
        for (const field of fields) {
            const fieldValid = await this.isFieldValid(field);
            if (!fieldValid) {
                console.log(`Field ${field} failed validation`);
                isValid = false;
            }
        }

        console.log("Model validation complete. Is valid:", isValid);
        return isValid;
    }

    public afterUpdate: undefined;
    public beforeUpdate: undefined;

    @action
    public async addDirectoryEntry(entry: ProjectDirectoryModel): Promise<ApiResult<ProjectDirectoryModelDTO>> {
        this.setIsLoading(true);
        const apiResult = await this.Post<ProjectDirectoryModelDTO>(AppUrls.Server.Projects.UpsertProjectDirectoryPerson, entry.toDto());
        if (apiResult.wasSuccessful) {
            runInAction(() => {
                const newEntry = new ProjectDirectoryModel();
                newEntry.fromDto(apiResult.payload);
                this.entries.push(newEntry);
            });
        }
        this.setIsLoading(false);
        return apiResult;
    }

    @action
    public async updateDirectoryEntry(entry: ProjectDirectoryModel): Promise<ApiResult<ProjectDirectoryModelDTO>> {
        this.setIsLoading(true);
        const apiResult = await this.Post<ProjectDirectoryModelDTO>(AppUrls.Server.Projects.UpsertProjectDirectoryPerson, entry.toDto());
        if (apiResult.wasSuccessful) {
            runInAction(() => {
                const index = this.entries.findIndex((e) => e.id === entry.id);
                if (index !== -1) {
                    const updatedEntry = new ProjectDirectoryModel();
                    updatedEntry.fromDto(apiResult.payload);
                    this.entries[index] = updatedEntry;
                }
            });
        }
        this.setIsLoading(false);
        return apiResult;
    }

    @action
    public async deleteDirectoryEntry(entryId: string): Promise<ApiResult<boolean>> {
        this.setIsLoading(true);
        const request = { id: entryId, sourceId: this.currentProjectId };
        const apiResult = await this.Post<boolean>(AppUrls.Server.Projects.DeleteProjectDirectoryPerson, request);
        if (apiResult.wasSuccessful) {
            runInAction(() => {
                const index = this.entries.findIndex((e) => e.id === entryId);
                if (index !== -1) {
                    this.entries[index].isDeleted = true;
                }
            });
        }
        this.setIsLoading(false);
        return apiResult;
    }
}
